import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import { TopNav } from '../components/TopNav';
import { AssistantChat } from '../components/AssistantChat';
import { Sheet, SheetContent } from '../components/Sheet';
import { cn } from '../lib/utils';
import { useAuthStore } from '../store/authStore';

export function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const isProvisional = useAuthStore((s) => s.user?.provisional);

  // Support is a normal grant-driven hub now, so the sidebar is always shown —
  // employees navigate it like any other hub (it used to be hidden for them).
  const isSupportModule = location.pathname === '/support' || location.pathname.startsWith('/support/');

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      {/* Desktop sidebar */}
      <div className="hidden md:flex h-full flex-col min-h-0">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed((value) => !value)} />
      </div>

      {/* Mobile sidebar (drawer) */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 p-0" hideClose>
          <Sidebar inSheet onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="relative flex min-w-0 flex-1 flex-col h-screen overflow-hidden">
        <TopNav onMenuClick={() => setMobileOpen(true)} />
        {isProvisional && (
          <div className="flex items-center gap-2 border-b border-warning/30 bg-warning/10 px-4 py-2 text-sm text-foreground md:px-6">
            <AlertTriangle className="size-4 shrink-0 text-warning" />
            <p className="leading-snug">
              You're signed in with a <span className="font-semibold">temporary Employee role</span>.
              Contact your <span className="font-semibold">Super Admin or Manager</span> to assign your
              proper role and unlock full access.
            </p>
          </div>
        )}
        <main
          key={location.pathname}
          className="flex-1 flex flex-col min-h-0 overflow-y-auto custom-scrollbar"
        >
          <div
            className={cn(
              'flex-1 flex flex-col min-h-0 animate-fade-in',
              isSupportModule ||
              location.pathname.startsWith('/assets/settings')
                ? 'w-full h-full p-0'
                : 'mx-auto w-full max-w-[1600px] px-4 py-4 md:px-8 md:py-6'
            )}
          >
            <Outlet />
          </div>
        </main>
      </div>

      {/* In-app assistant (check in/out, leave, attendance fixes) */}
      <AssistantChat />
    </div>
  );
}
